import React from 'react';
import { Link } from 'react-router-dom';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg font-body font-semibold transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground hover:bg-primary-light shadow-elegant hover:shadow-elegant-lg',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary-light shadow-gold',
        outline: 'border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground',
        outlineLight: 'border-2 border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary',
        ghost: 'text-primary hover:bg-primary/10',
        link: 'text-secondary underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-9 px-4 text-sm',
        md: 'h-11 px-6 text-base',
        lg: 'h-14 px-8 text-lg',
      },
      fullWidth: {
        true: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  href?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, fullWidth, href, icon, iconPosition = 'left', children, ...props }, ref) => {
    const content = (
      <>
        {icon && iconPosition === 'left' && icon}
        {children}
        {icon && iconPosition === 'right' && icon}
      </>
    );

    if (href) {
      const isExternal = href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:');
      
      if (isExternal) {
        return (
          <a
            href={href}
            className={cn(buttonVariants({ variant, size, fullWidth, className }))}
            target={href.startsWith('http') ? '_blank' : undefined}
            rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
          >
            {content}
          </a>
        );
      }
      
      return (
        <Link to={href} className={cn(buttonVariants({ variant, size, fullWidth, className }))}>
          {content}
        </Link>
      );
    }

    return (
      <button
        className={cn(buttonVariants({ variant, size, fullWidth, className }))}
        ref={ref}
        {...props}
      >
        {content}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button, buttonVariants };
